import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Layout from "./Layout";
import { useAuth } from "./contexts/AuthContext";

// Lazy-loaded pages for better performance
const Home = lazy(() => import("./pages/Home"));
const Careers = lazy(() => import("./pages/Careers"));
const WebApplications = lazy(() => import("./pages/WebApplications"));
const CloudService = lazy(() => import("./pages/CloudService"));
const QaAutomation = lazy(() => import("./pages/QaAutomation"));
const AIMLServices = lazy(() => import("./pages/AIMLServices"));
const DataVisualization = lazy(() => import("./pages/DataVisualization"));
const ManagedServices = lazy(() => import("./pages/ManagedServices"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ApplyJob = lazy(() => import("./pages/ApplyJob"));
const Profile = lazy(() => import("./pages/Profile"));

// Lazy-loaded 404 page
const NotFound = lazy(() => import("./pages/NotFound")); // 404 fallback page

// HCM Dashboards
const HCMRoutes = [
  // { path: "/dashboard/admin", Component: lazy(() => import("./components/HCM/Admin/Dashboard")) },
  { path: "/dashboard/analytics", Component: lazy(() => import("./components/HCM/Analytics/Dashboard")) },
  { path: "/dashboard/applicants", Component: lazy(() => import("./components/HCM/Applicants/Dashboard")) },
  { path: "/dashboard/benefits", Component: lazy(() => import("./components/HCM/Benefits/Dashboard")) },
  { path: "/dashboard/employee-development", Component: lazy(() => import("./components/HCM/EmployeeDevelopment/Dashboard")) },
  { path: "/dashboard/employee-management", Component: lazy(() => import("./components/HCM/EmployeeManagement/Dashboard")) },
  { path: "/dashboard/enterprise", Component: lazy(() => import("./components/HCM/Enterprise/Dashboard")) },
  { path: "/dashboard/hr-services", Component: lazy(() => import("./components/HCM/HRServices/Dashboard")) },
  { path: "/dashboard/interviews", Component: lazy(() => import("./components/HCM/Interviews/Dashboard")) },
  { path: "/dashboard/jobs", Component: lazy(() => import("./components/HCM/Jobs/Dashboard")) },
  { path: "/dashboard/learning-and-development", Component: lazy(() => import("./components/HCM/LearningAndDevelopment/Dashboard")) },
  { path: "/dashboard/messaging", Component: lazy(() => import("./components/HCM/Messaging/Dashboard")) },
  { path: "/dashboard/payroll", Component: lazy(() => import("./components/HCM/Payroll/Dashboard")) },
  { path: "/dashboard/performance-management", Component: lazy(() => import("./components/HCM/PerformanceManagement/Dashboard")) },
  { path: "/dashboard/recruiter", Component: lazy(() => import("./components/HCM/Recruiter/Dashboard")) },
  { path: "/dashboard/roles", Component: lazy(() => import("./components/HCM/Roles/Dashboard")) },
  { path: "/dashboard/settings", Component: lazy(() => import("./components/HCM/Settings/Dashboard")) },
  { path: "/dashboard/time-and-absence", Component: lazy(() => import("./components/HCM/TimeAndAbsence/Dashboard")) },
  { path: "/dashboard/users", Component: lazy(() => import("./components/HCM/Users/Dashboard")) },
  { path: "/dashboard/work-management", Component: lazy(() => import("./components/HCM/WorkManagement/Dashboard")) },
  { path: "/dashboard/subscription", Component: lazy(() => import("./components/HCM/Subscription/Dashboard")) },
  { path: "/dashboard/forms", Component: lazy(() => import("./components/HCM/Forms/Dashboard")) },
];

// Spinner Component for Suspense Fallback
const Spinner = () => <div className="spinner">Loading...</div>;

const ProtectedRoute = ({ element }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <Spinner />; // Show a spinner while loading
  }

  return user ? element : <Navigate to="/" replace />;
};

function App() {
  return (
    <Layout>
      <Suspense fallback={<Spinner />}>
        <Routes>
          {/* General Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/webapplications" element={<WebApplications />} />
          <Route path="/cloudservice" element={<CloudService />} />
          <Route path="/qaautomation" element={<QaAutomation />} />
          <Route path="/aimlservices" element={<AIMLServices />} />
          <Route path="/datavisualization" element={<DataVisualization />} />
          <Route path="/managedservices" element={<ManagedServices />} />
          <Route path="/apply/:jobId" element={<ApplyJob />} />
          <Route path="/profile" element={<Profile />} />

          {/* Dashboard Routes */}
          <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} />} />

          {/* HCM Dashboard Routes */}
          {HCMRoutes.map(({ path, Component }) => (
            <Route key={path} path={path} element={<ProtectedRoute element={<Component />} />} />
          ))}

          {/* Fallback for undefined routes */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}

export default App;
